<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { IconButton, Popover, PopoverContent, PopoverTrigger, Tooltip } from '@/shared/ui';
  import { MEMBER_TRAINING_STATUS_LABELS, type MemberTrainingStatus } from '@/entities/Training';
  import ReplaceIcon from '@/shared/assets/icons/ReplaceIcon.svg';

  const props = withDefaults(
    defineProps<{
      status: MemberTrainingStatus;
      loading?: boolean;
      /** Each table that renders this menu kept its own casing for the label; kept as-is on purpose. */
      label?: string;
    }>(),
    { loading: false, label: 'Mudar Status' }
  );

  const emit = defineEmits<{
    change: [status: MemberTrainingStatus];
  }>();

  const ALL_STATUSES: MemberTrainingStatus[] = ['PENDING', 'COMPLETED', 'NOT_COMPLETED'];

  const isOpen = ref(false);

  const items = computed(() =>
    ALL_STATUSES.filter((status) => status !== props.status).map((status) => ({
      id: status,
      label: MEMBER_TRAINING_STATUS_LABELS[status],
    }))
  );

  const ITEM_CLASS =
    'flex flex-shrink-0 whitespace-nowrap gap-3 w-full text-left px-4 py-2 text-md text-gray-07 font-medium hover:bg-gray-01';

  const handleItemClick = (status: MemberTrainingStatus) => {
    isOpen.value = false;
    emit('change', status);
  };
</script>

<template>
  <Popover v-model:open="isOpen">
    <PopoverTrigger as-child>
      <Tooltip
        :content="label"
        class="flex items-center"
      >
        <IconButton
          :src="ReplaceIcon"
          :alt="label"
          :disabled="loading"
          class="w-6 h-6"
        />
      </Tooltip>
    </PopoverTrigger>
    <PopoverContent
      data-testid="training-collaborator-status-menu"
      side="bottom"
      align="end"
      class="w-49 rounded-2xl border-0 bg-white-01 p-0 py-1 shadow-2xl"
    >
      <button
        v-for="item in items"
        :key="item.id"
        type="button"
        :class="ITEM_CLASS"
        @click="handleItemClick(item.id)"
      >
        {{ item.label }}
      </button>
    </PopoverContent>
  </Popover>
</template>
